import React from 'react';
import useGameStore from '../../store/gameStore';
import { formatMoney } from '../../utils/format';
import { TVA_RATES } from '../../data/taxes';
import { Receipt, CheckCircle2 } from 'lucide-react';

/**
 * Fiscalité — onglet TVA. Affiche les totaux période + bouton déclaration
 * + historique des déclarations passées. Extracted from Fiscalite.jsx on
 * 28/05/2026.
 */
export default function TvaTab() {
  const {
    tvaCollectedTotal, tvaDeductibleTotal, tvaHistory,
    tvaPendingDeclaration, payTvaDeclaration, tvaCredit, treasury,
  } = useGameStore();
  const netTva = tvaCollectedTotal - tvaDeductibleTotal;

  return (
    <div className="space-y-4">
      <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
        <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
          <Receipt className="w-4 h-4" /> TVA — Période en cours
        </h3>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-accent/10 rounded-lg p-3 text-center">
            <p className="text-[10px] text-slate-500">TVA collectée</p>
            <p className="text-lg font-bold text-accent">{formatMoney(Math.round(tvaCollectedTotal))}</p>
            <p className="text-[10px] text-slate-500">Sur revenus (20%)</p>
          </div>
          <div className="bg-money/10 rounded-lg p-3 text-center">
            <p className="text-[10px] text-slate-500">TVA déductible</p>
            <p className="text-lg font-bold text-money">{formatMoney(Math.round(tvaDeductibleTotal))}</p>
            <p className="text-[10px] text-slate-500">Sur charges éligibles</p>
          </div>
          <div className={`rounded-lg p-3 text-center ${netTva >= 0 ? 'bg-danger/10' : 'bg-money/10'}`}>
            <p className="text-[10px] text-slate-500">TVA nette</p>
            <p className={`text-lg font-bold ${netTva >= 0 ? 'text-danger' : 'text-money'}`}>
              {formatMoney(Math.abs(Math.round(netTva)))}
            </p>
            <p className="text-[10px] text-slate-500">{netTva >= 0 ? 'À payer' : 'Crédit'}</p>
          </div>
        </div>

        {tvaCredit > 0 && (
          <div className="bg-money/10 border border-money/40 rounded-lg p-3 mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-money" />
            <p className="text-xs text-money">Crédit de TVA reporté : <strong>{formatMoney(tvaCredit)}</strong></p>
          </div>
        )}

        {tvaPendingDeclaration && (
          <button
            onClick={payTvaDeclaration}
            disabled={treasury < Math.max(0, netTva)}
            className="w-full bg-accent text-white py-2.5 rounded-lg text-sm font-bold hover:bg-accent-hover disabled:opacity-40 transition-colors"
            data-testid="declare-tva-btn"
          >
            Déclarer et payer la TVA ({formatMoney(Math.max(0, Math.round(netTva)))})
          </button>
        )}

        {/* TVA rates info */}
        <div className="mt-4 border-t border-ink-600 pt-3">
          <p className="text-xs font-semibold text-slate-400 mb-2">Taux de TVA applicables</p>
          <div className="space-y-1">
            {Object.entries(TVA_RATES).map(([key, val]) => (
              <div key={key} className="flex justify-between text-xs">
                <span className="text-slate-500">{val.label}</span>
                <span className="font-medium text-slate-300">{(val.rate * 100).toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* TVA History */}
      {tvaHistory.length > 0 && (
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
          <h3 className="text-sm font-semibold text-slate-200 mb-3">Historique TVA</h3>
          <div className="max-h-48 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-ink-700/30 sticky top-0">
                <tr>
                  <th className="px-2 py-1.5 text-left text-slate-500">Période</th>
                  <th className="px-2 py-1.5 text-right text-slate-500">Collectée</th>
                  <th className="px-2 py-1.5 text-right text-slate-500">Déductible</th>
                  <th className="px-2 py-1.5 text-right text-slate-500">Payé</th>
                </tr>
              </thead>
              <tbody>
                {tvaHistory.slice().reverse().map((h) => (
                  <tr key={`${h.month}-${h.year}`} className="border-t border-ink-600">
                    <td className="px-2 py-1 text-slate-400">M{h.month} A{h.year}</td>
                    <td className="px-2 py-1 text-right text-accent">{formatMoney(h.collected)}</td>
                    <td className="px-2 py-1 text-right text-money">{formatMoney(h.deductible)}</td>
                    <td className="px-2 py-1 text-right font-medium text-danger">{formatMoney(h.paid)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
